import React, { useEffect, useState } from 'react';
import { Phone, Plus, Trash2, Check, Loader2, ShieldCheck, MessageSquare, Moon } from 'lucide-react';
import { api } from '../../lib/api';

// Text-notification numbers for the signed-in person: add a mobile, verify
// it with a texted code, pick which events it hears about, optional quiet
// hours. Shared by Settings → Notifications (staff) and My Profile
// (patients) — `events` decides which toggles appear.
const inputClass = 'px-3 py-[9px] bg-tertiary border border-strong rounded-[10px] text-primary-c text-[13px] font-[inherit] focus:outline-none focus:border-[#0DA7CA]';
const fmtPhone = (e164) => { const d = String(e164 || '').replace(/\D/g, ''); return d.length === 11 ? `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}` : e164; };

export default function SmsNumbers({ events, intro, orgId = undefined }) {
  const [numbers, setNumbers] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [canManageOrg, setCanManageOrg] = useState(false);
  const [smsEnabled, setSmsEnabled] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ phone: '', label: '', scope: 'user' });
  const [codes, setCodes] = useState({}); // id → typed code
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [note, setNote] = useState('');

  const load = async () => {
    try { const r = await api.listMyNumbers(orgId); setNumbers(r.numbers || []); setSuggestions(r.suggestions || []); setCanManageOrg(!!r.can_manage_org); setSmsEnabled(r.sms_enabled !== false); }
    catch (e) { setError(e.message); setNumbers([]); }
  };
  useEffect(() => { load(); }, []);

  const add = async (preset) => {
    setBusy('add'); setError(''); setNote('');
    try {
      const n = await api.addMyNumber(preset ? { phone: preset.phone, label: preset.label, scope: 'org' } : { phone: form.phone, label: form.label, scope: form.scope || 'user' }, orgId);
      if (preset) setSuggestions((s) => s.filter((x) => x.phone !== preset.phone));
      setNumbers((prev) => [...(prev || []).filter((x) => x.id !== n.id), n]);
      setForm({ phone: '', label: '', scope: 'user' }); setAdding(false);
      setNote(n.code_delivery === 'demo' ? 'Demo: the code is in the Message Center.' : n.code_delivery === 'sent' ? `We texted a 6-digit code to ${fmtPhone(n.phone)}.` : `Could not text the code (${n.code_delivery}).`);
    } catch (e) { setError(e.message); }
    finally { setBusy(''); }
  };
  const confirm = async (n) => {
    setBusy(n.id); setError('');
    try { const u = await api.confirmMyNumber(n.id, codes[n.id] || '', orgId); setNumbers((prev) => prev.map((x) => (x.id === n.id ? u : x))); setNote(`${fmtPhone(n.phone)} is verified.`); }
    catch (e) { setError(e.message); }
    finally { setBusy(''); }
  };
  const resend = async (n) => {
    setBusy(n.id); setError('');
    try { const r = await api.resendMyNumberCode(n.id, orgId); setNote(r.code_delivery === 'demo' ? 'Demo: the new code is in the Message Center.' : 'A new code is on its way.'); }
    catch (e) { setError(e.message); }
    finally { setBusy(''); }
  };
  const remove = async (n) => {
    if (!window.confirm(`Remove ${fmtPhone(n.phone)}? It will stop receiving texts.`)) return;
    setBusy(n.id);
    try { await api.deleteMyNumber(n.id, orgId); setNumbers((prev) => prev.filter((x) => x.id !== n.id)); }
    catch (e) { setError(e.message); }
    finally { setBusy(''); }
  };
  const toggle = async (n, key) => {
    const next = { ...(n.events || {}), [key]: n.events?.[key] === false };
    setNumbers((prev) => prev.map((x) => (x.id === n.id ? { ...x, events: next } : x)));
    try { await api.updateMyNumber(n.id, { events: next }, orgId); } catch (e) { setError(e.message); }
  };
  const quiet = async (n, patch) => {
    const u = { ...n, ...patch };
    setNumbers((prev) => prev.map((x) => (x.id === n.id ? u : x)));
    try { await api.updateMyNumber(n.id, { quiet_start: u.quiet_start, quiet_end: u.quiet_end }, orgId); } catch (e) { setError(e.message); }
  };

  if (numbers === null) return <p className="text-[12.5px] text-muted-c flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Loading…</p>;

  return (
    <div>
      {intro && <p className="text-[12.5px] text-muted-c mb-4 max-w-[560px]">{intro}</p>}
      {!smsEnabled && <div className="text-[12px] text-warning bg-warning-soft rounded-[10px] px-3.5 py-2.5 mb-3">Texting is not switched on for this environment yet. Numbers you add will start receiving texts once it is.</div>}
      {error && <div className="text-[12px] text-danger bg-danger-soft rounded-[10px] px-3.5 py-2.5 mb-3">{error}</div>}
      {note && <div className="text-[12px] text-success bg-success-soft rounded-[10px] px-3.5 py-2.5 mb-3">{note}</div>}

      {orgId && suggestions.length > 0 && canManageOrg && (
        <div className="mb-4 rounded-[14px] border border-dashed border-strong bg-tertiary/40 p-4">
          <div className="text-[12.5px] font-semibold text-primary-c mb-1">Office numbers on file</div>
          <p className="text-[11.5px] text-muted-c mb-2.5">These are the phone numbers on your profile and locations. Set one up and it becomes the default place this practice's texts go — the whole team sees it here. It must be a mobile or text-capable line; we send a code to check.</p>
          <div className="flex flex-wrap gap-2">
            {suggestions.map((s) => (
              <button key={s.phone} onClick={() => add(s)} disabled={busy === 'add'} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[10px] text-[12px] font-semibold text-accent-light bg-accent-glow border border-theme disabled:opacity-50">
                <Phone size={11} /> Use {fmtPhone(s.phone)} <span className="text-muted-c font-normal">· {s.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="space-y-3">
        {numbers.map((n) => (
          <div key={n.id} className="rounded-[14px] border border-theme bg-elevated p-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3 min-w-0">
                <div className={`w-[34px] h-[34px] rounded-[10px] flex items-center justify-center shrink-0 ${n.verified ? 'bg-success-soft text-success' : 'bg-warning-soft text-warning'}`}><Phone size={14} /></div>
                <div className="min-w-0">
                  <div className="text-[13.5px] font-semibold text-primary-c">{fmtPhone(n.phone)}{n.label ? <span className="text-muted-c font-normal"> · {n.label}</span> : null}{n.scope === 'org' && <span className="ml-2 px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-accent-glow text-accent-light align-middle">Practice number</span>}</div>
                  <div className="text-[11.5px] text-muted-c">
                    {n.opted_out ? 'Replied STOP — not receiving texts' : n.verified ? <span className="inline-flex items-center gap-1"><ShieldCheck size={11} className="text-success" /> Verified{n.last_sent_at ? ` · last text ${new Date(n.last_sent_at).toLocaleDateString()}` : ''}</span> : 'Waiting for the code'}
                  </div>
                </div>
              </div>
              {n.can_edit !== false && <button onClick={() => remove(n)} disabled={busy === n.id} className="text-muted-c hover:text-danger" title="Remove"><Trash2 size={14} /></button>}
            </div>

            {!n.verified && !n.opted_out && n.can_edit !== false && (
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <input value={codes[n.id] || ''} onChange={(e) => setCodes((c) => ({ ...c, [n.id]: e.target.value.replace(/\D/g, '').slice(0, 6) }))} placeholder="6-digit code" inputMode="numeric" className={`${inputClass} w-[140px] tracking-[0.2em]`} />
                <button onClick={() => confirm(n)} disabled={busy === n.id || (codes[n.id] || '').length !== 6} className="px-3.5 py-2 rounded-[10px] text-[12.5px] font-semibold text-white gradient-accent shadow-glow disabled:opacity-50 inline-flex items-center gap-1.5">{busy === n.id ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Verify</button>
                <button onClick={() => resend(n)} disabled={busy === n.id} className="text-[12px] font-semibold text-accent-light">Resend code</button>
              </div>
            )}

            {n.verified && !n.opted_out && (
              <>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
                  {events.map(([key, label, hint]) => {
                    const on = n.events?.[key] !== false;
                    return (
                      <label key={key} className="flex items-start gap-2.5 cursor-pointer py-1">
                        <input type="checkbox" checked={on} disabled={n.can_edit === false} onChange={() => toggle(n, key)} className="mt-0.5 accent-[#0DA7CA]" />
                        <span className="min-w-0"><span className="block text-[12.5px] text-primary-c">{label}</span><span className="block text-[11px] text-muted-c">{hint}</span></span>
                      </label>
                    );
                  })}
                </div>
                <div className="mt-3 flex items-center gap-2 flex-wrap text-[12px] text-muted-c">
                  <Moon size={12} /> Quiet hours
                  <input type="time" value={n.quiet_start || ''} onChange={(e) => quiet(n, { quiet_start: e.target.value || null })} className={`${inputClass} w-[118px] py-1.5`} />
                  <span>to</span>
                  <input type="time" value={n.quiet_end || ''} onChange={(e) => quiet(n, { quiet_end: e.target.value || null })} className={`${inputClass} w-[118px] py-1.5`} />
                  {(n.quiet_start || n.quiet_end) && <button onClick={() => quiet(n, { quiet_start: null, quiet_end: null })} className="text-accent-light font-semibold">Clear</button>}
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {adding ? (
        <div className="mt-3 rounded-[14px] border border-theme bg-elevated p-4">
          <div className="flex items-center gap-2 flex-wrap">
            <input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder="(305) 555-0100" inputMode="tel" autoFocus className={`${inputClass} w-[170px]`} />
            <input value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} placeholder="Label (Front desk, My cell)" className={`${inputClass} w-[220px]`} />
            {orgId && canManageOrg && (
              <select value={form.scope} onChange={(e) => setForm((f) => ({ ...f, scope: e.target.value }))} className={`${inputClass} w-[190px]`} title="Who this number is for">
                <option value="user">My own number</option>
                <option value="org">Practice number (whole team)</option>
              </select>
            )}
            <button onClick={() => add()} disabled={busy === 'add' || form.phone.replace(/\D/g, '').length < 10} className="px-3.5 py-2 rounded-[10px] text-[12.5px] font-semibold text-white gradient-accent shadow-glow disabled:opacity-50 inline-flex items-center gap-1.5">{busy === 'add' ? <Loader2 size={13} className="animate-spin" /> : <MessageSquare size={13} />} Text me a code</button>
            <button onClick={() => setAdding(false)} className="text-[12px] text-muted-c">Cancel</button>
          </div>
          <p className="text-[11px] text-muted-c mt-2">By adding a number you agree to receive MediLink notification texts at it. Msg &amp; data rates may apply. Reply STOP any time.</p>
        </div>
      ) : (
        <button onClick={() => { setAdding(true); setNote(''); }} className="mt-3 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-accent-light hover:text-accent"><Plus size={13} /> Add a number</button>
      )}
    </div>
  );
}
